import { Button, Col, Modal, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { FormattedMessage, useIntl } from 'react-intl';
import { Key } from 'antd/es/table/interface';
import { useCustomerReducedList } from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerReduced,
  ListCustomersQuery,
} from '@/core/domain/people/Customers/Customers.types';
import { applyMask } from '@/utils/mask';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ModalProps } from '@/types/ModalProps';

import CustomersFiltersTableModal from './CustomersFiltersTableModal';
import CustomersTableModal from './CustomersTableModal';

import { CheckOutlined } from '@ant-design/icons';
import { useDebounce } from '@/hooks';

interface SelectedCustomerProps extends ModalProps {
  finishCallback?: (customerIds?: number[]) => void;
  selectedCustomerIds?: number[];
  noSelection?: boolean;
  onCustomerSelect?: (customer: CustomerReduced) => void;
}

const CustomersSelectedModal: React.FC<SelectedCustomerProps> = ({
  visible,
  finishCallback,
  handleVisible,
  selectedCustomerIds = [],
  noSelection,
  onCustomerSelect,
}) => {
  const intl = useIntl();
  const { Title } = Typography;
  const [queryParams, setQueryParams] = useState<ListCustomersQuery>({
    order: 'ASC',
    active: true,
  });

  const [selectedCustomersKeys, setSelectedCustomersKeys] = useState<Key[]>([]);

  const queryDebounce = useDebounce(queryParams);
  const { data, isFetching, hasNextPage, fetchNextPage } =
    useCustomerReducedList(queryDebounce, visible);

  const customers = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      setSelectedCustomersKeys(selectedCustomerIds);
    }
  }, [visible]);

  const handleOk = (): void => {
    if (finishCallback) {
      finishCallback(selectedCustomersKeys as number[]);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams({ order: 'ASC', active: true });

    setSelectedCustomersKeys([]);

    handleVisible();
  };

  const columns: ColumnsType<CustomerReduced> = [
    {
      dataIndex: 'name',
      title: <FormattedMessage id="customers.list.header.name" />,
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'fantasyName',
      title: <FormattedMessage id="customers.list.header.fantasyName" />,
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'cpfCnpj',
      title: <FormattedMessage id="customers.list.header.cpfCnpj" />,
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        if (value?.length === 11) {
          return applyMask(value, cpfMask);
        }
        return applyMask(value, cnpjMask);
      },
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'foreignDocument',
      title: 'Documento estrangeiro',
      ellipsis: true,
      width: '40%',
      render: (value: string) => {
        if (!value) {
          return '-';
        }

        return value;
      },
    },
    onCustomerSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onCustomerSelect(row)}
              icon={<CheckOutlined />}
            />
          ),
          width: '20%',
        }
      : {},
  ];

  const handleCustomersSelect = (selectedRowKeys: Key[]) => {
    setSelectedCustomersKeys(selectedRowKeys);
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'customers.modal.selectedCustomers.title.add',
      })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      destroyOnClose
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                <FormattedMessage id="customers.modal.selectedCustomers.search" />
              </Title>
            </div>

            <CustomersFiltersTableModal setQueryParams={setQueryParams} />

            <CustomersTableModal
              noSelection={noSelection}
              isFetching={isFetching}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              columns={columns}
              customers={customers}
              selectedCustomersKeys={selectedCustomersKeys}
              handleCustomersSelect={handleCustomersSelect}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default CustomersSelectedModal;
